import { BuiLabel, type BuiLabelProps } from "@btnet/blueprint-components";

import { BTPopover } from "../BTPopover/BTPopover";

type IBTLabelBaseProps = Omit<
  BuiLabelProps,
  "infoContent" | "ariaLabel" | "text"
> & {
  /** Message to display in a popover triggered by an info icon next to the label text. */
  info?: string;
};

export type IBTLabelProps = IBTLabelBaseProps &
  ({ text: string } | { ariaLabel: string });

/**
 * BTLabel associates a text label with an input or other interactive element.
 * It can be aligned to the left or the top of the element and supports a required indicator
 * and an optional info tooltip.
 *
 * @param props.text - Label text to display (required when ariaLabel is not provided)
 * @param props.ariaLabel - Accessible label for screen readers (required when text is not provided)
 * @param props.align - Position of the label relative to children: "left" or "top". Defaults to
 *   "top" when no children are present, "left" otherwise.
 * @param props.htmlFor - id of the associated input element (wires the label to its control)
 * @param props.id - id attribute for the label element itself
 * @param props.testid - data-testid attribute for testing
 * @param props.className - Additional CSS class names
 * @param props.isRequired - When true, shows an asterisk (*) required indicator after the label text
 * @param props.size - Font size of the label text (defaults to "distinct-sm", or "normal-md" when labelAfter)
 * @param props.children - Associated element (e.g. an input) rendered alongside the label
 * @param props.labelAfter - When true, renders the label text after the children instead of before
 * @param props.outerRef - Ref for the outer label element
 * @param props.info - Message shown in an info tooltip icon next to the label text
 *
 * @example
 * // Left-aligned label wrapping an input (default)
 * <BTLabel text="First name" htmlFor="first-name" isRequired>
 *   <BTInput id="first-name" ... />
 * </BTLabel>
 *
 * @example
 * // Top-aligned label with an info tooltip
 * <BTLabel text="Notes" htmlFor="notes" align="top" info="Add any relevant notes here">
 *   <BTTextArea id="notes" ... />
 * </BTLabel>
 *
 * @example
 * // Label rendered after its child (e.g. checkbox)
 * <BTLabel text="I agree to the terms" htmlFor="agree" labelAfter>
 *   <BTCheckbox id="agree" ... />
 * </BTLabel>
 */
export function BTLabel({ info, ...rest }: IBTLabelProps) {
  const infoContent = info ? <BTPopover content={info} /> : undefined;
  return <BuiLabel {...(rest as BuiLabelProps)} infoContent={infoContent} />;
}
